import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Boxes,
  Calculator,
  ChevronDown,
  ClipboardCheck,
  Cog,
  Factory,
  FileStack,
  GitBranch,
  LayoutGrid,
  Link2,
  Shield,
  ShieldCheck,
  Truck,
  User,
  Users,
} from "lucide-react";

import { INTEGRATOR_MODULE_DEFINITIONS } from "@hub-modules";

import { INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER } from "@hub-modules";

import { useNavigationTrail } from "@/lib/navigationTrailContext";

import { HoverHintContent, HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";
import { useIntegratorMe } from "@/lib/me-query";
import { appsVisibleInAppSwitcher } from "@/lib/subapp-access";
import { resolveAppSwitcherChipApp } from "./appSwitcherChip";
import {
  APP_SWITCHER_MENU_Z_INDEX,
  computeHorizontalMenuPanel,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";

const APP_MENU_ICONS: Record<string, LucideIcon> = {
  erp: Link2,
  mrp: Factory,
  clampqc: ClipboardCheck,
  manufacturing: Cog,
  manufacturing_management: Boxes,
  documents: FileStack,
  wiki: BookOpen,
  bom: GitBranch,
  admin: Shield,
  shipping: Truck,
  accounting: Calculator,
  quality: ShieldCheck,
  art_and_labels: FileStack,
  personal: User,
  people: Users,
};

function menuIconForKey(key: string): LucideIcon {
  return APP_MENU_ICONS[key] ?? LayoutGrid;
}

export interface AppDescriptor {
  key: string;
  label: string;
  path: string;
  description: string;
}

/** App switcher list — derived from `src/integrator-modules.ts` (same as `GET /api/integrator-modules`). */
export const APPS: readonly AppDescriptor[] = INTEGRATOR_MODULE_DEFINITIONS;

export function AppSwitcher({ activeKey }: { activeKey: string }) {
  const { resetNavigationTrail } = useNavigationTrail();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuSurfaceRef = useRef<HTMLUListElement | null>(null);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const { data: me } = useIntegratorMe();
  const apps = [...appsVisibleInAppSwitcher(me)].sort((left, right) =>
    left.label.localeCompare(right.label, undefined, { sensitivity: "base" }),
  );
  const active = resolveAppSwitcherChipApp(activeKey, apps);
  const ActiveMenuIcon = active ? menuIconForKey(active.key) : LayoutGrid;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      if (menuSurfaceRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    const reposition = (): void => {
      if (!triggerRef.current) {
        setMenuBox(null);
        return;
      }
      setMenuBox(
        computeHorizontalMenuPanel({
          anchorRect: triggerRef.current.getBoundingClientRect(),
          menuWidth: 18 * 16,
          horizontalAlign: "start",
          gap: 4,
          maxHeightCap: Math.min(520, Math.floor(window.innerHeight * 0.88)),
        }),
      );
    };
    reposition();
    const onLayout = (): void => {
      reposition();
    };
    return registerViewportLayoutListeners(onLayout);
  }, [open]);

  if (apps.length === 0) {
    return (
      <HoverHintContent hoverHint="No modules assigned for your role." className="text-sm font-semibold text-slate-500">
        No modules
      </HoverHintContent>
    );
  }

  return (
    <div ref={containerRef} className="relative flex h-9 shrink-0 items-stretch rounded-md border border-slate-300 bg-white">
      <Link
        to={active?.path ?? "/"}
        aria-label={`${active?.label ?? "Module"} home`}
        onClick={() => {
          setOpen(false);
          resetNavigationTrail();
        }}
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-l-md px-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-100"
      >
        <ActiveMenuIcon className="h-4 w-4 shrink-0 text-brand-600" aria-hidden />
        <span className="whitespace-nowrap">{active?.label ?? "Apps"}</span>
      </Link>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Switch module"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex w-9 items-center justify-center rounded-r-md border-l border-slate-300 text-slate-600 hover:bg-slate-100"
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && menuBox
        ? createPortal(
            <ul
              ref={menuSurfaceRef}
              role="menu"
              className="overflow-y-auto rounded-md border border-slate-200 bg-white py-1.5 text-sm shadow-lg"
              style={{
                position: "fixed",
                top: menuBox.top,
                left: menuBox.left,
                width: menuBox.width,
                maxHeight: menuBox.maxHeight,
                zIndex: APP_SWITCHER_MENU_Z_INDEX,
              }}
            >
          {apps.map((app) => {
            const isActive = app.key === activeKey;
            const ItemIcon = menuIconForKey(app.key);
            return (
              <li key={app.key} role="none">
                <HoverHintTrigger
                  align="start"
                  trigger={
                    <Link
                      role="menuitem"
                      to={app.path}
                      aria-label={`${app.label}. ${app.description}`}
                      onClick={() => {
                        resetNavigationTrail();
                        setOpen(false);
                        try {
                          window.localStorage.setItem("lastApp", app.key);
                        } catch {
                          // local storage may be disabled; ignore
                        }
                      }}
                      className={cn(
                        "mx-1 flex items-center gap-2.5 rounded-md px-2.5 py-2 font-medium transition-colors",
                        isActive
                          ? "bg-brand-50 text-brand-900"
                          : "text-slate-900 hover:bg-slate-100",
                      )}
                    >
                      <ItemIcon
                        className={cn(
                          "h-4 w-4 shrink-0",
                          isActive ? "text-brand-600" : "text-slate-500",
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0">{app.label}</span>
                    </Link>
                  }
                >
                  <span>{app.description}</span>
                </HoverHintTrigger>
              </li>
            );
          })}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}

export function rememberLastApp(key: string): void {
  if (INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER.has(key)) {
    return;
  }
  try {
    window.localStorage.setItem("lastApp", key);
  } catch {
    // local storage may be disabled; ignore
  }
}

export function readLastApp(): string {
  try {
    const raw = window.localStorage.getItem("lastApp") ?? "erp";
    if (raw === "users") return "admin";
    return raw;
  } catch {
    return "erp";
  }
}

/** Last app switcher module, never avatar-only personal. */
export function readLastOperationalAppKey(): string {
  const raw = readLastApp();
  if (INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER.has(raw)) {
    return "erp";
  }
  return raw;
}
